/**
 * Matter-action contracts for The Surface's case detail panel — the firm-decision
 * band (accept / decline / pull on the Handling Firm's behalf) and the funnel-
 * terminal close-case stamp.
 *
 * The state gates are the set of Placement Decisions from which each action is
 * valid. They live here, in shared, so the route enforces them server-side
 * against the live placement AND the panel uses the same source of truth to
 * decide which buttons to show.
 *
 * ACCEPT_GATE / DECLINE_GATE mirror the Case Portal worker's gates — keep them
 * identical to the portal; they are the operating contract. PULL_GATE is
 * CC-native: the portal has no pull handler. Its gate
 * mirrors DECLINE_GATE because pull and decline are the same kind of move — a
 * terminal "this firm is out" decision the `lf20.declined` workflow consumes
 * identically (reroute / reject / Pending) — they differ only in intent and the
 * Decline Reason. See ADR-0064.
 *
 * CLOSE_GATE is a **deliberate divergence** from the portal (ADR-0070): the
 * portal allows close-case from any decision (a server-side gap UI discipline
 * currently fills); CC tightens to `{Placed, Settled}` because closing a
 * Lead-Sent or Reviewing placement is nonsensical — there's no settlement to
 * close. In the ADR-0064 spirit: byte-equivalent to the portal except where
 * the portal carries a known defect, which CC corrects.
 */

import { z } from "zod";

/** Placement Decisions from which a placement may be ACCEPTED. Already-Accepted
 *  is handled as an idempotent no-op by the route, not listed here. */
export const ACCEPT_GATE = [
  "Sent",
  "Reviewing",
] as const;

/** Placement Decisions from which a placement may be DECLINED. Wider than the
 *  accept gate: a firm can decline after acknowledging (Accepted) or before any
 *  outreach (Assigned). */
export const DECLINE_GATE = [
  "Assigned",
  "Sent",
  "Reviewing",
  "Accepted",
] as const;

/** Placement Decisions from which a placement may be PULLED. Currently mirrors
 *  DECLINE_GATE — both are "firm is out" terminal moves the `lf20.declined`
 *  workflow consumes the same way. Kept as its own constant (not an alias) so the
 *  two intents are independently nameable if they ever diverge. Includes Accepted
 *  on purpose: pulling a firm that accepted but stalled (e.g. "Accepted, FSA
 *  outstanding") is the highest-value pull (ADR-0059's revenue blind spot) — and
 *  it is exactly the case the writer's Decision Date null-out makes safe. */
export const PULL_GATE = [
  "Assigned",
  "Sent",
  "Reviewing",
  "Accepted",
] as const;

/** True when a placement at `decision` may be accepted (or is already Accepted —
 *  the idempotent case the route absorbs). */
export function canAccept(decision: string | null): boolean {
  return (
    decision === "Accepted" ||
    (decision !== null && (ACCEPT_GATE as readonly string[]).includes(decision))
  );
}

/** True when a placement at `decision` may be declined. */
export function canDecline(decision: string | null): boolean {
  return decision !== null && (DECLINE_GATE as readonly string[]).includes(decision);
}

/** True when a placement at `decision` may be pulled. */
export function canPull(decision: string | null): boolean {
  return decision !== null && (PULL_GATE as readonly string[]).includes(decision);
}

/** Placement Decisions from which a placement may have **Case Closed Date**
 *  stamped. CC tightens the portal's empty gate (allow-any) to `{Placed,
 *  Settled}` — closing a Lead-Sent or Reviewing placement is nonsensical, no
 *  settlement to close. The route also checks `caseClosedDate === null`
 *  against the live placement to absorb already-closed retries. See the
 *  close-case-gate ADR landing with slice 5a. */
export const CLOSE_GATE = ["Placed", "Settled"] as const;

/** True when a placement at `decision` may be closed. The route additionally
 *  guards against a placement that is already closed (live `Case Closed Date`
 *  set — 409). */
export function canClose(decision: string | null): boolean {
  return decision !== null && (CLOSE_GATE as readonly string[]).includes(decision);
}

/** Body for POST /api/placements/:id/close. The close reason is OPTIONAL
 *  (ADR-0075): the portal *requires* it (400 if missing) and Slack-notifies
 *  the first 200 chars; CC makes it optional, audits the text when supplied,
 *  and does not Slack. The body may be absent or `{}` (a bodyless close stays
 *  valid — no regression); a present-but-blank `close_reason: ""` is rejected
 *  (`.trim().min(1)`) so empty strings never reach the audit row. */
export const CloseRequestSchema = z.object({
  close_reason: z.string().trim().min(1).optional(),
});
export type CloseRequest = z.infer<typeof CloseRequestSchema>;

/** Body for POST /api/placements/:id/decline. The reason is required and bounded,
 *  mirroring the portal's declineLead (max 5000 chars). Accept takes no body. */
export const DeclinePlacementRequestSchema = z.object({
  reason: z.string().min(1).max(5000),
});
export type DeclinePlacementRequest = z.infer<typeof DeclinePlacementRequestSchema>;

// ---------------------------------------------------------------------------
// Slice 5b — settlement-band actions (confirm LF / OF payment + edit settlement)
// ---------------------------------------------------------------------------

/** Placement Decisions from which an LF Payment Date may be stamped.
 *  Settlement-band only: confirming LF received its share is meaningful
 *  only once the matter has Settled. The route additionally guards an
 *  idempotent re-confirm (live `LF Payment Date` already set → 200 no-op). */
export const CONFIRM_LF_PAYMENT_GATE = ["Settled"] as const;

/** Placement Decisions from which an OF Payment Date may be stamped. Same
 *  shape as the LF gate; an additional precondition (`LF Payment Date`
 *  set — narrow-409 before the gate per ADR-0070) is enforced at the
 *  route against the live placement. */
export const CONFIRM_OF_PAYMENT_GATE = ["Settled"] as const;

/** Placement Decisions from which any of Settlement Amount / Attorney Fee
 *  Rate / Settlement Date may be amended. Settlement-band only — there is
 *  nothing to correct on a pre-Settled placement. */
export const EDIT_SETTLEMENT_GATE = ["Settled"] as const;

/** True when a placement at `decision` may have LF Payment Date stamped. */
export function canConfirmLfPayment(decision: string | null): boolean {
  return (
    decision !== null &&
    (CONFIRM_LF_PAYMENT_GATE as readonly string[]).includes(decision)
  );
}

/** True when a placement at `decision` may have OF Payment Date stamped.
 *  Note: the route additionally requires `lfPaymentDate !== null` against
 *  the live placement — not encoded here because this gate is the *state*
 *  contract (which Decisions allow it at all). The LF-set precondition is
 *  a runtime check against payment progression. */
export function canConfirmOfPayment(decision: string | null): boolean {
  return (
    decision !== null &&
    (CONFIRM_OF_PAYMENT_GATE as readonly string[]).includes(decision)
  );
}

/** True when a placement at `decision` may have its settlement edited. */
export function canEditSettlement(decision: string | null): boolean {
  return (
    decision !== null &&
    (EDIT_SETTLEMENT_GATE as readonly string[]).includes(decision)
  );
}

/** Upper bound on the Handling Firm's attorney-fee rate on a settlement.
 *  60% — verified against portal `worker.js` (line 2167, `ATTORNEY_FEE_RATE_MAX`).
 *  Used both by `EditSettlementRequestSchema` validation and by the panel
 *  form to bound the input. */
export const ATTORNEY_FEE_RATE_MAX = 0.6;

/** Body for POST /api/placements/:id/edit-settlement. Mirrors the portal's
 *  `editSettlementDetails` field validation:
 *    - settlementAmount: must be > 0 and finite. Currency on the record.
 *    - attorneyFeeRate: decimal in (0, ATTORNEY_FEE_RATE_MAX]. Cap mirrors portal.
 *    - settlementDate: ISO date YYYY-MM-DD only (no datetime).
 *  All three are optional; the schema rejects the empty body (`.refine`
 *  at-least-one) so the route can return 400 invalid_body uniformly. The
 *  no-op short-circuit (every supplied value equals current) is a route
 *  concern — it depends on the live placement, not the request. */
export const EditSettlementRequestSchema = z
  .object({
    settlementAmount: z
      .number()
      .finite()
      .nonnegative()
      .optional(),
    attorneyFeeRate: z
      .number()
      .finite()
      .nonnegative()
      .max(ATTORNEY_FEE_RATE_MAX)
      .optional(),
    settlementDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
  })
  .refine(
    (v) =>
      v.settlementAmount !== undefined ||
      v.attorneyFeeRate !== undefined ||
      v.settlementDate !== undefined,
    {
      message:
        "Provide at least one of settlementAmount, attorneyFeeRate, settlementDate.",
    },
  );
export type EditSettlementRequest = z.infer<typeof EditSettlementRequestSchema>;
